/**
 * PWA Service Worker Registration and Management
 */

export function registerServiceWorker(): void {
  // Don't register service worker in development mode
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('[PWA] Service Worker disabled in development mode');

    // Unregister any existing service workers and clear caches
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
          console.log('[PWA] Unregistered existing service worker');
        });
      });

      // Clear all caches in development
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
          console.log('[PWA] Cleared cache:', cacheName);
        });
      });
    }
    return;
  }

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('[PWA] Service Worker registered:', registration.scope);

          // Check for updates periodically
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000); // Check every hour

          // Handle service worker updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker available
                console.log('[PWA] New version available! Refresh to update.');

                // Optionally notify user
                if (window.confirm('A new version is available! Reload to update?')) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                  window.location.reload();
                }
              }
            });
          });
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });

      // Handle service worker controller change (new version activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[PWA] New service worker activated');
      });
    });
  }
}

export function unregisterServiceWorker(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        return Promise.all(
          registrations.map((registration) => registration.unregister())
        ).then(() => true);
      })
      .catch((error) => {
        console.error('[PWA] Service Worker unregistration failed:', error);
        return false;
      });
  }
  return Promise.resolve(false);
}

export function clearServiceWorkerCache(): void {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
  }
}

/**
 * Check if app is running in standalone mode (installed as PWA)
 */
export function isPWA(): boolean {
  // @ts-ignore - displayMode is a valid property
  return window.matchMedia('(display-mode: standalone)').matches ||
         // @ts-ignore - standalone is a valid property on navigator
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
}

/**
 * Check if device supports PWA installation
 */
export function canInstallPWA(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}
